// src/lib/viz.ts — visualization tokens and formatters.
//
// The categorical palette is the validated default from the dataviz reference
// instance. Both modes pass the lightness band, chroma floor, adjacent-pair CVD
// separation (worst ΔE 9.1 light / 8.4 dark) and the normal-vision floor
// (19.6 / 19.3). Light mode carries a contrast WARN on three slots, so the relief
// rule applies: every chart ships direct labels AND a table view.
//
// Slots are assigned in FIXED ORDER and never cycled. A ninth series folds into
// "Other" rather than inventing a hue.

export const SERIES_LIGHT = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
] as const;

export const SERIES_DARK = [
  '#3987e5', '#d95926', '#199e70', '#c98500',
  '#d55181', '#008300', '#9085e9', '#e66767',
] as const;

/** Reserved status colours. Never reused as "series 4"; always paired with a label. */
export const STATUS = {
  good: '#1baf7a',
  warning: '#eda100',
  serious: '#eb6834',
  critical: '#e34948',
  neutral: '#94a3b8',
} as const;

/** Sequential ramp for magnitude (one hue, light to dark). */
export const SEQUENTIAL = ['#f4ecdf', '#e8d9c1', '#d8c09d', '#bea58b', '#a88662', '#74593d'] as const;

export const AXIS_COLOR = '#cbc6bd';
export const GRID_COLOR = '#ebe7e0';
export const TEXT_MUTED = '#9e9a93';

export const seriesColor = (index: number) => SERIES_LIGHT[index % SERIES_LIGHT.length]!;

// ── Formatters ──────────────────────────────────────────────────────────────

export function formatMoney(minor: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: Math.abs(minor) >= 100_000 ? 0 : 2,
  }).format(minor / 100);
}

/** Compact money for axis ticks: £1.2m, £340k. */
export function formatMoneyCompact(minor: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(minor / 100);
}

export const formatNumber = (n: number) => new Intl.NumberFormat('en-GB').format(n);
export const formatPercent = (n: number) => `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;

export function formatDate(value: string | number | Date): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export type ColumnType = 'string' | 'number' | 'money' | 'date' | 'percent';

export function formatCell(value: unknown, type: ColumnType, currency = 'GBP'): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (type) {
    case 'money': return formatMoney(Number(value), currency);
    case 'percent': return formatPercent(Number(value));
    case 'number': return formatNumber(Number(value));
    case 'date': return formatDate(value as string);
    default: return String(value);
  }
}

/** Axis-tick formatter, kept compact so labels never collide. */
export function tickFormatter(type: ColumnType, currency = 'GBP') {
  return (value: unknown) => {
    if (value === null || value === undefined) return '';
    switch (type) {
      case 'money': return formatMoneyCompact(Number(value), currency);
      case 'percent': return `${Number(value)}%`;
      case 'number': return new Intl.NumberFormat('en-GB', { notation: 'compact' }).format(Number(value));
      case 'date': {
        const d = new Date(value as string);
        return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      }
      default: return String(value);
    }
  };
}
